import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, MessageSquare } from "lucide-react";
import { API } from "../config";
import { useAuth } from "../context/AuthContext";
import { SmsTopupCard } from "../components/admin/smscredit/SmsTopupCard";
import { SmsLedgerTable } from "../components/admin/smscredit/SmsLedgerTable";
import { SmsCountryStats } from "../components/admin/smscredit/SmsCountryStats";
import { SmsAdminTools } from "../components/admin/smscredit/SmsAdminTools";
import { SmsPriceList } from "../components/admin/smscredit/SmsPriceList";
import { SmsMonthlyReport } from "../components/admin/smscredit/SmsMonthlyReport";
import { SmsUsageChart } from "../components/admin/smscredit/SmsUsageChart";

export const formatEur = (n, d = 2) =>
  `€ ${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: d, maximumFractionDigits: d })}`;

const useStripeReturn = (onPaid) => {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("stripe_session_id");
    if (params.get("stripe_cancelled")) {
      toast.info("Ricarica annullata");
      window.history.replaceState({}, "", window.location.pathname);
    }
    if (!sid) return;
    window.history.replaceState({}, "", window.location.pathname);
    let tries = 0;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/webhook/stripe/checkout-status/${sid}`);
        if (r.data.payment_status === "paid") {
          toast.success("Pagamento ricevuto: credito SMS ricaricato");
          onPaid();
          return;
        }
        if (r.data.status === "expired") { toast.error("Sessione di pagamento scaduta"); return; }
      } catch { /* retry */ }
      if (++tries < 10) setTimeout(poll, 2000);
      else toast.warning("Pagamento in verifica: il credito apparirà a breve");
    };
    toast.loading("Verifica pagamento in corso…", { duration: 3000 });
    poll();
  }, [onPaid]);
};

export default function SmsCreditPage() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/admin/sms-credit/summary`);
    setSummary(r.data);
    setReloadKey((k) => k + 1);
  }, []);

  useEffect(() => { load().catch(() => toast.error("Impossibile caricare il credito SMS")); }, [load]);
  useStripeReturn(load);

  if (!summary) return <div className="p-6 text-gray-400" data-testid="sms-credit-loading">Caricamento…</div>;

  return (
    <div className="p-6 max-w-7xl mx-auto animate-fade-in space-y-6" data-testid="sms-credit-page">
      <div className="flex items-center gap-3">
        <MessageSquare className="text-lojack-red" size={28} />
        <div>
          <h1 className="text-2xl font-bold text-white">Credito SMS</h1>
          <p className="text-sm text-gray-400">Credito prepagato per gli SMS inviati da SecureWallet a clienti e concessionari</p>
        </div>
      </div>
      {(summary.low || summary.running_out) && (
        <div className={`rounded-xl border p-4 flex items-start gap-3 ${summary.exhausted ? "border-red-500/50 bg-red-500/10" : "border-amber-500/40 bg-amber-500/10"}`} data-testid="sms-credit-low-banner">
          <AlertTriangle className={summary.exhausted ? "text-red-400" : "text-amber-400"} size={20} />
          <p className="text-sm text-white">
            {summary.exhausted
              ? "Credito esaurito: gli SMS ai clienti sono sospesi e OTP e link alla card vengono inviati solo via email. Ricarica per riattivarli."
              : summary.low
                ? `Credito sotto la soglia di avviso (${formatEur(summary.threshold_eur)}). Ricarica per evitare la sospensione degli SMS.`
                : `Al ritmo attuale il credito finisce tra circa ${summary.days_left} giorni. Ricarica per evitare la sospensione degli SMS.`}
          </p>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2"><SmsTopupCard summary={summary} /></div>
        <SmsAdminTools summary={summary} isSuper={user?.role === "super_admin"} onChanged={load} />
      </div>
      <SmsUsageChart reloadKey={reloadKey} />
      <SmsMonthlyReport />
      <SmsPriceList />
      <SmsCountryStats />
      <SmsLedgerTable reloadKey={reloadKey} />
    </div>
  );
}
